import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Play } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, errMsg, fmtTs, inr, num, pct, tone } from "@/lib/api";
import { PriceChart } from "@/components/PriceChart";

export const Metric = ({ label, value, cls = "text-slate-100", tid }) => (
  <div className="panel p-3">
    <div className="widget-title">{label}</div>
    <div className={`font-mono text-lg mt-1 ${cls}`} data-testid={tid}>{value}</div>
  </div>
);

const exportCsv = (trades, name) => {
  const rows = [["Entry Time", "Exit Time", "Entry", "Exit", "Qty", "PnL", "PnL %", "Reason"],
    ...trades.map((t) => [fmtTs(t.entry_time), fmtTs(t.exit_time), t.entry_price, t.exit_price, t.qty, t.pnl, t.pnl_pct, t.reason])];
  const blob = new Blob([rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = `${name}-trades.csv`; a.click();
};

export const Equity = ({ data }) => (
  <ResponsiveContainer width="100%" height={240}>
    <AreaChart data={data} margin={{ left: 10, right: 10, top: 10 }}>
      <defs>
        <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#22A34A" stopOpacity={0.25} /><stop offset="100%" stopColor="#22A34A" stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid stroke="#102A4A" />
      <XAxis dataKey="time" tickFormatter={fmtTs} stroke="#475569" fontSize={10} minTickGap={60} />
      <YAxis stroke="#475569" fontSize={10} tickFormatter={(v) => `₹${(v / 100000).toFixed(1)}L`} domain={["auto", "auto"]} width={60} />
      <Tooltip contentStyle={{ background: "#0B1F3A", border: "1px solid #163056", fontSize: 11 }} labelFormatter={fmtTs} formatter={(v, n) => [inr(v), n]} />
      <Area type="monotone" dataKey="equity" name="Strategy" stroke="#22A34A" fill="url(#eq)" strokeWidth={1.5} />
      <Line type="monotone" dataKey="benchmark" name="Buy & Hold" stroke="#FFAB00" dot={false} strokeWidth={1} strokeDasharray="4 3" />
    </AreaChart>
  </ResponsiveContainer>
);

export default function BacktestLab({ initial }) {
  const [list, setList] = useState([]);
  const [strategy, setStrategy] = useState(initial);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (st) => {
    if (!st) return;
    setBusy(true);
    try {
      const { id, created_at, _k, ...b } = st;
      const { data } = await api.post("/backtest", b);
      setRes(data);
      toast.success(`Backtest complete: ${data.metrics.total_trades} trades`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  useEffect(() => {
    api.get("/strategies").then((r) => {
      setList(r.data);
      if (!initial && r.data.length) setStrategy(r.data[0]);
    });
    if (initial) run(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const m = res?.metrics;
  return (
    <div className="space-y-3 rise" data-testid="backtest-lab">
      <div className="panel p-3 flex flex-wrap items-center gap-3">
        <h1 className="font-display text-xl font-bold text-white tracking-tight mr-2">Backtest Lab</h1>
        <select data-testid="backtest-strategy-select" className="field min-w-[240px]" value={strategy?.id || ""}
          onChange={(e) => { setStrategy(list.find((x) => x.id === e.target.value)); setRes(null); }}>
          {!strategy?.id && strategy && <option value="">{`${strategy.name} (unsaved)`}</option>}
          {list.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        {strategy && <span className="font-mono text-[11px] text-slate-500">{strategy.symbol} · {strategy.timeframe} · SL {strategy.stop_loss_pct}% · TP {strategy.take_profit_pct}%</span>}
        <button data-testid="backtest-run-button" onClick={() => run(strategy)} disabled={busy || !strategy} className="btn-primary ml-auto">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run Backtest
        </button>
      </div>

      {!res && !busy && <div className="panel p-10 text-center text-sm text-slate-500" data-testid="backtest-empty">Select a strategy and run a backtest on simulated NSE history.</div>}
      {busy && !res && <div className="panel p-10 text-center text-sm text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Running simulation…</div>}

      {res && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="backtest-metrics">
            <Metric label="Net Return" tid="metric-total-return" value={inr(m.total_return, 0)} cls={tone(m.total_return)} />
            <Metric label="Return %" tid="metric-return-pct" value={pct(m.total_return_pct)} cls={tone(m.total_return_pct)} />
            <Metric label="Buy & Hold" tid="metric-buy-hold" value={pct(m.buy_hold_return_pct)} cls={tone(m.buy_hold_return_pct)} />
            <Metric label="Win Rate" tid="metric-win-rate" value={`${num(m.win_rate_pct)}%`} />
            <Metric label="Profit Factor" tid="metric-profit-factor" value={num(m.profit_factor)} cls={m.profit_factor >= 1 ? "text-bull" : "text-bear"} />
            <Metric label="Max Drawdown" tid="metric-max-dd" value={`${num(m.max_drawdown_pct)}%`} cls="text-bear" />
            <Metric label="Sharpe" tid="metric-sharpe" value={num(m.sharpe)} />
            <Metric label="Trades W/L" tid="metric-trades" value={`${m.total_trades} · ${m.wins}/${m.losses}`} />
          </div>
          <div className="panel">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#163056]">
              <span className="widget-title">Equity Curve vs Buy & Hold</span>
              <span className="font-mono text-[10px] text-slate-500">CAGR {pct(m.cagr_pct)} · {m.bars_tested} bars · avg hold {m.avg_trade_bars} bars</span>
            </div>
            <Equity data={res.equity_curve} />
          </div>
          <div className="panel">
            <div className="px-3 py-2 border-b border-[#163056] widget-title">Trade Markers · {strategy.symbol} {strategy.timeframe}</div>
            <PriceChart symbol={strategy.symbol} tf={strategy.timeframe} ind={{ ema9: true, ema21: true }} markers={res.markers} limit={1500} height={340} />
          </div>
          <div className="panel">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#163056]">
              <span className="widget-title">Trade Log ({res.trades.length})</span>
              <button data-testid="backtest-export-csv" onClick={() => exportCsv(res.trades, strategy.name)} className="btn-ghost h-7"><Download className="w-3.5 h-3.5" /> Export CSV</button>
            </div>
            <div className="max-h-80 overflow-auto">
              <table className="tbl w-full" data-testid="backtest-trades-table">
                <thead><tr><th>#</th><th>Entry</th><th>Exit</th><th>Entry ₹</th><th>Exit ₹</th><th>Qty</th><th>P&L</th><th>%</th><th>Reason</th></tr></thead>
                <tbody>
                  {[...res.trades].reverse().map((t, i) => (
                    <tr key={i}>
                      <td className="text-slate-500">{res.trades.length - i}</td><td>{fmtTs(t.entry_time)}</td><td>{fmtTs(t.exit_time)}</td>
                      <td>{num(t.entry_price)}</td><td>{num(t.exit_price)}</td><td>{t.qty}</td>
                      <td className={tone(t.pnl)}>{inr(t.pnl)}</td><td className={tone(t.pnl_pct)}>{pct(t.pnl_pct)}</td>
                      <td className="text-slate-400">{t.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
